import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Lock, Camera, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewApprovePanel = ({ month, year, authHeaders, isOwner, storageAreas }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState({});
  const [lightboxPhotoId, setLightboxPhotoId] = useState(null);

  const areas = storageAreas || [];

  const fetchReview = useCallback(async () => {
    if (!isOwner) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/review`, { params: { month, year }, headers: authHeaders });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load review data");
    } finally {
      setLoading(false);
    }
  }, [month, year, authHeaders, isOwner]);

  const fetchPhotos = useCallback(async () => {
    if (!isOwner) return;
    try {
      const res = await axios.get(`${API}/inventory/storage-photos`, { params: { month, year }, headers: authHeaders });
      setPhotos(res.data.photos || []);
    } catch (err) {
      console.error("Error fetching storage photos:", err);
    }
  }, [month, year, authHeaders, isOwner]);

  useEffect(() => { fetchReview(); fetchPhotos(); }, [fetchReview, fetchPhotos]);

  const loadPreview = useCallback(async (photoId) => {
    setPreviews((prev) => {
      if (prev[photoId]) return prev;
      axios.get(`${API}/inventory/storage-photos/${photoId}/download`, { headers: authHeaders, responseType: "blob" })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          setPreviews((p) => ({ ...p, [photoId]: url }));
        })
        .catch((err) => console.error("Error loading photo preview:", err));
      return prev;
    });
  }, [authHeaders]);

  useEffect(() => { photos.forEach((p) => loadPreview(p.id)); }, [photos, loadPreview]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await axios.post(`${API}/inventory/review/approve`, {}, { params: { month, year }, headers: authHeaders });
      toast.success("Month approved and locked");
      setConfirmOpen(false);
      fetchReview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to approve");
    } finally {
      setApproving(false);
    }
  };

  if (!isOwner) {
    return (
      <p data-testid="review-not-owner" className="text-sm text-slate-500 py-6 text-center">
        Only Owners and Co-Owners can review and approve inventory counts.
      </p>
    );
  }

  if (loading) {
    return <p className="text-sm text-slate-400 py-6 text-center">Loading review data...</p>;
  }

  const rows = data?.rows || [];

  return (
    <div data-testid="review-approve-panel" className="space-y-4">
      {data?.approved ? (
        <div data-testid="review-approved-banner" className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-800">
          <Lock className="w-4 h-4" /> Approved by {data.approvedBy} on {new Date(data.approvedAt).toLocaleDateString()} — Shopping List is frozen. Counts and stock additions can still be edited.
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {data?.countedItems || 0} of {data?.totalItems || 0} items counted this month.
          </p>
          <Button data-testid="open-approve-dialog-button" onClick={() => setConfirmOpen(true)} disabled={rows.length === 0}>
            Approve & Lock
          </Button>
        </div>
      )}

      {photos.length > 0 && (
        <div data-testid="review-storage-photos-section" className="border border-slate-200 rounded-xl p-4 space-y-3">
          <h4 className="font-medium text-slate-800 flex items-center gap-2">
            <Camera className="w-4 h-4" /> Storage Area Photos
          </h4>
          {areas.map((area) => {
            const areaPhotos = photos.filter((p) => p.storageArea === area.key);
            if (areaPhotos.length === 0) return null;
            return (
              <div key={area.key} data-testid={`review-photos-area-${area.key}`}>
                <p className="text-xs font-medium text-slate-500 mb-2">{area.label} ({areaPhotos.length})</p>
                <div className="flex flex-wrap gap-3">
                  {areaPhotos.map((photo) => (
                    <button key={photo.id} type="button" data-testid={`review-photo-thumb-${photo.id}`}
                      onClick={() => setLightboxPhotoId(photo.id)}>
                      {previews[photo.id] ? (
                        <img src={previews[photo.id]} alt={photo.originalFilename}
                          className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                      ) : (
                        <div className="w-20 h-20 rounded-lg border border-slate-200 bg-slate-50 animate-pulse" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lightboxPhotoId && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setLightboxPhotoId(null)}>
          <button type="button" className="absolute top-4 right-4 text-white" onClick={() => setLightboxPhotoId(null)} data-testid="close-review-photo-lightbox">
            <X className="w-6 h-6" />
          </button>
          {previews[lightboxPhotoId] && (
            <img src={previews[lightboxPhotoId]} alt="Storage area" className="max-h-[85vh] max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />
          )}
        </div>
      )}

      <div className="border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">Current Amount</th>
              <th className="text-left px-3 py-2">Quantity to Buy</th>
              <th className="text-left px-3 py-2">Notes</th>
              <th className="text-left px-3 py-2">Counted By</th>
              <th className="text-left px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.itemId} data-testid={`review-row-${row.itemId}`}
                className={`border-t border-slate-100 ${row.flagged ? "bg-amber-50/60" : ""}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{row.canonicalName}</td>
                <td className="px-3 py-2">{row.currentAmount ?? <span className="text-slate-400">Not counted</span>}</td>
                <td className="px-3 py-2">{row.quantityToBuy ?? "—"}</td>
                <td className="px-3 py-2 text-slate-500">{row.notes || "—"}</td>
                <td className="px-3 py-2 text-slate-500 text-xs">{row.countedBy || "—"}</td>
                <td className="px-3 py-2">
                  {row.flagged && (
                    <span data-testid={`flag-badge-${row.itemId}`} title={row.flagReason}
                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      <AlertTriangle className="w-3 h-3" /> Worth a look
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                  No items confirmed for this month yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="approve-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve and lock {rows.length} item{rows.length !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              Once approved, counts for this month can no longer be edited. The shopping list will be generated from
              the Quantity to Buy values entered. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approve-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="approve-confirm-ok" disabled={approving} onClick={handleApprove}>
              Approve & Lock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReviewApprovePanel;
